import { Component, OnDestroy, OnInit } from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { combineLatest, merge, Observable, of } from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    mapTo,
    take,
    takeWhile,
    tap
} from 'rxjs/operators';
import clone from 'just-clone';
import { LoggingService } from '../../shared/services/logging';
import * as fromDataServices from '../../data-services';
import * as fromCompanySettings from '../../company-setting';
import * as fromShared from '../../shared';
import * as fromUser from '../../user-info';
import { ActionsSubject, select, Store } from '@ngrx/store';
import { mapObj, StateService } from '@uirouter/core';
import { ImportService } from '../../import/import.service';
import { UnsavedChangesService } from '../../shared/modal-dialog';
import moment from 'moment';
import { ofType } from '@ngrx/effects';
import { IUserInfo } from '../../user-info';

@Component({
    selector: 'ef-employee-validation-container',
    templateUrl: './employee-validation-container.component.html',
    styleUrls: ['./employee-validation-container.component.scss'],
    standalone: false
})
export class EmployeeValidationContainerComponent implements OnInit, OnDestroy {
    process$: Observable<fromShared.Process>;
    public nameOrder$: Observable<string>;
    public userInfo$: Observable<IUserInfo>;
    public lastSyncLists$: Observable<Date | undefined>;
    public lastValidation$: Observable<Date>;
    public validationCompleteDate$: Observable<Date>;
    EmployeeValidationModelDS: EntityCollectionService<fromDataServices.EmployeeValidation.EmployeeValidationModel>;
    EmployeeValidationModel$: Observable<
        fromDataServices.EmployeeValidation.EmployeeValidationModel[]
    >;
    PayrollValidationModelDS: EntityCollectionService<fromDataServices.PayrollValidation.PayrollValidationModel>;
    PayrollValidationModel$: Observable<
        fromDataServices.PayrollValidation.PayrollValidationModel[]
    >;
    // loading$: Observable<boolean>;
    observables$: Observable<boolean>;
    IsLoading = true;
    active = true;
    constructor(
        private log: LoggingService,
        private confirmservice: UnsavedChangesService,
        public entityServices: EntityServices,
        private importService: ImportService,
        private ss: StateService,
        private dispatcher: ActionsSubject,
        private store: Store
    ) {
        this.log.verbose({ type: 'EmployeeValidationContainerComponent::' });
        this.EmployeeValidationModelDS =
            entityServices.getEntityCollectionService<fromDataServices.EmployeeValidation.EmployeeValidationModel>(
                fromDataServices.EmployeeValidation.entityName
            );
        this.PayrollValidationModelDS =
            entityServices.getEntityCollectionService<fromDataServices.PayrollValidation.PayrollValidationModel>(
                fromDataServices.PayrollValidation.entityName
            );
    }
    ngOnDestroy(): void {
        this.active = false;
    }
    ngOnInit(): void {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.userInfo$ = this.store.pipe(select(fromUser.getUserInfo));

        this.lastSyncLists$ = this.store.pipe(
            select(fromCompanySettings.getSettingDateValue('LastQBLists'))
        );
        this.nameOrder$ = this.store.pipe(
            select(fromCompanySettings.getSettingValue('EmployeeNameOrder'))
        );
        this.lastValidation$ = this.store.pipe(
            select(
                fromCompanySettings.getSettingDateValue(
                    'ExternalEmployeeValidationLastRun'
                )
            ),
            tap(x =>
                this.log.verbose({
                    type: 'EmployeeValidationContainerComponent',
                    ExternalEmployeeValidationLastRun: x
                })
            ),
            map(x => x ?? new Date(0)),
            tap(x =>
                this.log.verbose({
                    type: 'EmployeeValidationContainerComponent',
                    ExternalEmployeeValidationLastRun: x
                })
            )
        );
        this.validationCompleteDate$ = this.store.pipe(
            select(
                fromCompanySettings.getSettingDateValue(
                    'ExternalEmployeeValidationComplete'
                )
            ),
            distinctUntilChanged((x, y) => moment(x).isSame(y)),
            tap(x => {
                this.log.verbose({
                    type: 'EmployeeValidationContainerComponent',
                    ExternalEmployeeValidationComplete: x
                });
                if (x) {
                    this.store.dispatch(new fromUser.LoadUserPermissions());
                }
            }),
            map(x => x || new Date(0))
        );

        this.dispatcher
            .pipe(
                takeWhile(() => this.active),
                ofType(fromUser.UserActionTypes.SetUserPermissions)
            )
            .subscribe(() => {
                this.ss.go('main');
            });

        this.updateData();

        combineLatest([
            this.process$.pipe(filter(x => !!x)),
            this.EmployeeValidationModel$.pipe(filter(x => !!x)),
            this.PayrollValidationModel$.pipe(filter(x => !!x)),
            this.lastSyncLists$.pipe(filter(x => !!x)),
            this.lastValidation$.pipe(filter(x => !!x)),
            this.validationCompleteDate$.pipe(filter(x => !!x)),
            this.nameOrder$.pipe(filter(x => !!x))
        ])
            .pipe(take(1))
            .subscribe(() => (this.IsLoading = false));
    }

    updateData() {
        this.EmployeeValidationModelDS.clearCache();
        this.PayrollValidationModelDS.clearCache();
        this.EmployeeValidationModelDS.getAll();
        this.PayrollValidationModelDS.getAll();
        this.EmployeeValidationModel$ =
            this.EmployeeValidationModelDS.entities$.pipe(
                map(items => [...clone(items)])
            );

        this.PayrollValidationModel$ =
            this.PayrollValidationModelDS.entities$.pipe(
                map(items => [...clone(items)])
            );

        combineLatest([
            this.EmployeeValidationModelDS.loading$,
            this.PayrollValidationModelDS.loading$
        ])
            .pipe(map(([a, b]) => a || b))
            .subscribe(
                x => (this.IsLoading = x), //next
                () => {
                    this.IsLoading = false;
                }, //error
                () => (this.IsLoading = false) //complete
            );

        this.store.dispatch(
            fromCompanySettings.getCompanySetting({
                SettingType: 'EmployeeNameOrder'
            })
        );
        this.store.dispatch(
            fromCompanySettings.getCompanySetting({
                SettingType: 'LastQBLists'
            })
        );
        this.store.dispatch(
            fromCompanySettings.getCompanySetting({
                SettingType: 'ExternalEmployeeValidationLastRun'
            })
        );
        this.store.dispatch(
            fromCompanySettings.getCompanySetting({
                SettingType: 'ExternalEmployeeValidationComplete'
            })
        );
    }

    onValidate() {
        this.log.verbose({
            type: 'EmployeeValidationContainerComponent:onValidate'
        });
        this.doImport();
    }
    onComplete() {
        this.log.verbose({
            type: 'EmployeeValidationContainerComponent:onComplete'
        });
        this.confirmservice
            .confirmPromise({
                dialogText:
                    'Are you sure? This action will enable the eFAACTGovConPay Interface.',
                okText: 'OK',
                cancelText: 'Cancel'
            })
            .then(() => {
                this.store.dispatch(
                    fromCompanySettings.putCompanySettings({
                        settings: [
                            {
                                SettingType:
                                    fromCompanySettings.CompanySettingEnum
                                        .ExternalEmployeeValidationComplete,
                                SettingValue: moment().format('YYYY-MM-DD')
                            },
                            {
                                SettingType:
                                    fromCompanySettings.CompanySettingEnum
                                        .ExternalEmployeeSource,
                                SettingValue: 1
                            }
                        ]
                    })
                );
            });
    }

    doImport(): void {
        const includeInactive = true;
        //log
        this.log.verbose({
            type: 'EmployeeValidationContainerComponent',
            message: 'onStartImport',
            includeInactive
        });
        // call the import API
        this.IsLoading = true;
        // this.message = 'Importing...';
        // this.messages = [
        //     {
        //         severity: 'info',
        //         summary: 'Importing...'
        //     }
        // ];
        this.importService
            .LoadData(includeInactive)
            .then(() => {
                this.updateData();
                // this.message = 'Import Complete';
                // this.messages = [
                //     {
                //         severity: 'success',
                //         summary: 'Import Complete'
                //     }
                // ];
                // this.store.dispatch(
                //     fromSettings.putCompanySettings({
                //         settings: [
                //             {
                //                 SettingType: 'GovConPayImportInactiveEmployees',
                //                 SettingValue: false
                //             }
                //         ]
                //     })
                // );
            })
            .catch(reason => {
                this.log.error({
                    type: 'EmployeeValidationContainerComponent',
                    reason
                });
                // this.message = 'Import Failed';
                // this.messages = [
                //     {
                //         severity: 'error',
                //         summary: 'Import Failed'
                //     }
                // ];
            })
            .finally(() => (this.IsLoading = false));
    }
}
